import { useCallback, useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/shallow";
import { useLocation } from "react-router-dom";
import debounce from "lodash.debounce";
import Icon from "@mdi/react";
import { mdiAccountSearchOutline, mdiClose, mdiMagnify } from "@mdi/js";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
} from "@mui/material";

import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import useNotifier from "hooks/useNotifier";
import getNameInitials from "utils/getNameInitials";
import { StorageBuckets } from "api/storage";
import ProfileApi, { ProfileRow } from "api/profile";
import FriendApi from "api/friend";
import useBoundStore from "store";
import styles from "./addFriend.module.scss";

let abortController = new AbortController();

export interface AddFriendProps {
  onBack?: () => void;
}

function AddFriend(props: AddFriendProps) {
  const { onBack } = props;
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [friendsIds, setFriendsIds] = useState<number[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const userAuth = useBoundStore((state) => state.user.auth);
  const { friends, friendRequests } = useBoundStore(
    useShallow((state) => ({
      friends: state.appData.friends,
      friendRequests: state.appData.friendRequests,
    }))
  );
  const { showNotification } = useNotifier();
  const location = useLocation();
  const profileApi = useMemo(() => new ProfileApi(showNotification), [showNotification]);
  const friendApi = useMemo(() => new FriendApi(showNotification), [showNotification]);
  const sentRequestsIds = friendRequests.map((request) => request.uid);

  /**
   * Fetch user profiles based on the provided name
   */
  const fetchUsersProfiles = useCallback(
    async (name: string) => {
      if (friendsIds) {
        setIsLoading(true);
        abortController.abort();
        abortController = new AbortController();
        const usersProfiles = await profileApi.searchUsersProfiles(name, abortController.signal, friendsIds);
        setIsLoading(false);

        if (usersProfiles) {
          setProfiles(usersProfiles);
        }
      }
    },
    [profileApi, friendsIds]
  );

  /**
   * Debounce the search of users profiles for better user experience
   */
  const debouncedSearch = useMemo(
    () => debounce((value) => fetchUsersProfiles(value.replace(" ", ".")), 400),
    [fetchUsersProfiles]
  );

  /**
   * Handle search input change
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearchTerm(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  /**
   * Clear search input
   */
  function handleClear() {
    fetchUsersProfiles("");
    setSearchTerm("");
  }

  /**
   * Send friend request
   *
   * @param profile
   */
  async function handleSendFriendRequest(profile: ProfileRow): Promise<void> {
    await friendApi.sendFriendRequest(userAuth!.id, profile.id);
  }

  const renderEndAdornmentContent = () => {
    if (isLoading) {
      return <CircularProgress className={styles.circularProgress} size={24} />;
    }

    return searchTerm ? (
      <IconButton aria-label='Clear search' onClick={handleClear}>
        <Icon path={mdiClose} size={1} />
      </IconButton>
    ) : null;
  };

  /**
   * If user wants to add a new user using the shared link check
   * to see if he reached the friends limit
   */
  useEffect(() => {
    const searchQuery = new URLSearchParams(location.search);

    if (searchQuery.get("name") && searchQuery.get("hash")) {
      const tag = `${searchQuery.get("name")}#${searchQuery.get("hash")}`;
      setSearchTerm(tag);
      fetchUsersProfiles(tag);
    } else {
      fetchUsersProfiles("");
    }
  }, [location.search, fetchUsersProfiles]);

  /**
   * Push authId to friendsIds so that the profile of the auth user won't be shown and
   * keep friendsIds up to date in case a user accept the request before leaving the
   * Add friend screen
   */
  useEffect(() => {
    const currentFriendsIds = friends.map((friend) => friend.uid);

    if (userAuth) {
      currentFriendsIds.push(userAuth.id);
    }

    setFriendsIds(currentFriendsIds);
  }, [friends, userAuth]);

  return (
    <PageLayout appBar={<AppBar title='Add friend' onBack={onBack} />}>
      <Box p={2} pt={3}>
        <TextField
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Icon path={mdiMagnify} size={1} />
              </InputAdornment>
            ),
            endAdornment: <InputAdornment position='start'>{renderEndAdornmentContent()}</InputAdornment>,
          }}
          label='Search user'
          onChange={handleChange}
          placeholder='Search by name or tag'
          value={searchTerm}
          // variant='outlined'
        />
      </Box>
      {!isLoading && profiles.length === 0 && (
        <DataIconPlaceholder
          icon={mdiAccountSearchOutline}
          text="Sorry, we couldn't find any users with that name. Please try again."
        />
      )}
      {isLoading ? (
        <DataIconPlaceholder icon={mdiAccountSearchOutline} text='Searching users...' />
      ) : (
        <List>
          {profiles.map((profile) => (
            <ListItem className={styles.listItem} key={profile.id}>
              <ListItemAvatar>
                <AsyncAvatar
                  avatarName={profile.avatar}
                  bucket={StorageBuckets.PROFILES_AVATARS}
                  loadSmallSize
                  placeholder={getNameInitials(profile.name)}
                />
              </ListItemAvatar>
              <ListItemText primary={profile.name} secondary={profile.tag} />

              <ListItemSecondaryAction>
                <Chip
                  color={sentRequestsIds.includes(profile.id) ? "default" : "primary"}
                  disabled={sentRequestsIds.includes(profile.id)}
                  label={sentRequestsIds.includes(profile.id) ? "Request sent" : "Send request"}
                  onClick={() => handleSendFriendRequest(profile)}
                  size='small'
                  variant='outlined'
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </PageLayout>
  );
}

export default AddFriend;
